import { Logo } from "./Logo.tsx";
import { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes, useParams } from "react-router-dom";
import { api, ApiError } from "./api.ts";
import { Board, LANES, useBoard, type Lane } from "./pages/Board.tsx";
import { FilePage } from "./pages/File.tsx";
import { Catalogue } from "./pages/Catalogue.tsx";
import { Editor } from "./pages/Editor.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";
import { Activity } from "./pages/Activity.tsx";
import { CalendarPage } from "./pages/Calendar.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { Guests } from "./pages/Guests.tsx";
import { NewBooking } from "./pages/NewBooking.tsx";
import { Hosts } from "./pages/Hosts.tsx";
import { Login } from "./pages/Login.tsx";
import { Messages, Payouts, Refunds } from "./pages/Money.tsx";

type Me = { user: { name: string; role: string } };

function Lanes({ rows, err }: { rows: ReturnType<typeof useBoard>["rows"]; err: boolean }) {
  const { lane = "requested" } = useParams();
  return <Board rows={rows} lane={lane as Lane} err={err} />;
}

export function App() {
  const [me, setMe] = useState<Me | null | "none">(null);
  const check = useCallback(async () => {
    try { setMe(await api<Me>("/v1/account/me")); } catch (e) { setMe(e instanceof ApiError && e.status === 401 ? "none" : "none"); }
  }, []);
  useEffect(() => { void check(); }, [check]);
  const board = useBoard();

  if (me === null) return <div className="boot"><span className="wordmark"><Logo height={44} /></span></div>;
  if (me === "none") return <Login onDone={check} />;
  if (me.user.role !== "staff" && me.user.role !== "root") return <div className="boot"><p>Accès réservé à l'équipe Sekani.</p><button className="btn ghost" onClick={async () => { await api("/auth/sign-out", { body: {} }).catch(() => undefined); setMe("none"); }}>Changer de compte</button></div>;

  const count = (l: Lane) => (board.rows ?? []).filter((b) => (l === "verify" ? b.payments.some((p) => p.kind !== "refund" && p.status === "submitted") : l === "awaiting_payment" ? b.status === l && !b.payments.some((p) => p.kind !== "refund" && p.status === "submitted") : b.status === l)).length;

  return (
    <div className="layout">
      <nav className="side-nav" aria-label="Navigation">
        <span className="wordmark"><Logo height={44} /></span>
        <NavLink to="/" end className="nav"><span>Tableau de bord</span></NavLink>
        <NavLink to="/calendar" className="nav"><span>Calendrier</span></NavLink>
        <span className="eyebrow">Réservations</span>
        <NavLink to="/new" className="nav"><span>+ Nouvelle réservation</span></NavLink>
        {LANES.map((l) => (
          <NavLink key={l.id} to={`/q/${l.id}`} className="nav">
            <span>{l.label}</span><span className="count num">{board.rows ? count(l.id) : ""}</span>
          </NavLink>
        ))}
        <span className="eyebrow">Biens</span>
        <NavLink to="/catalogue" className="nav"><span>Catalogue</span></NavLink>
        <NavLink to="/hosts" className="nav"><span>Propriétaires</span></NavLink>
        <NavLink to="/guests" className="nav"><span>Clients</span></NavLink>
        <span className="eyebrow">Argent & messages</span>
        <NavLink to="/payouts" className="nav"><span>Versements</span></NavLink>
        <NavLink to="/refunds" className="nav"><span>Remboursements</span></NavLink>
        <NavLink to="/messages" className="nav"><span>Messages</span></NavLink>
        <NavLink to="/activity" className="nav"><span>Journal</span></NavLink>
        <ThemeToggle />
        <button className="link small muted" onClick={async () => { await api("/auth/sign-out", { body: {} }).catch(() => undefined); setMe("none"); }}>{me.user.name} · Quitter</button>
      </nav>
      <main>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/new" element={<NewBooking />} />
          <Route path="/guests" element={<Guests />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/q/:lane" element={<Lanes rows={board.rows} err={board.err} />} />
          <Route path="/b/:id" element={<FilePage onChange={board.reload} />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/catalogue/new" element={<Editor />} />
          <Route path="/catalogue/:id" element={<Editor key={location.pathname} />} />
          <Route path="/hosts" element={<Hosts />} />
          <Route path="/payouts" element={<Payouts />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/messages" element={<Messages />} />
        </Routes>
      </main>
    </div>
  );
}
